/**
 * CR306: Firecrawl recipe config types + test result types
 */

// ─── Recipe Config (persisted in recipe_config JSONB) ───

export interface FirecrawlRecipeConfig {
  render_js?: boolean;
  wait_ms?: number;
  timeout_ms?: number;
  consent?: ConsentConfig;
  offer_safe?: OfferSafeConfig;
  actions?: RecipeAction[];
  debug?: DebugConfig;
}

export interface ConsentConfig {
  enabled?: boolean;
  selectors?: string[];
}

export interface OfferSafeConfig {
  enabled?: boolean;
  block_patterns?: string[];
}

export type RecipeActionType = 'click' | 'scroll' | 'wait' | 'screenshot' | 'write' | 'press';

export interface RecipeAction {
  type: RecipeActionType;
  selector?: string;
  wait_ms?: number;
  /** For 'write' actions: text to type (supports {{postal_code}} tokens) */
  text?: string;
  /** For 'press' actions: key name (Enter, Tab, Escape) */
  key?: string;
}

export interface DebugConfig {
  verbose?: boolean;
  include_screenshot?: boolean;
}

// ─── Defaults ───

export const DEFAULT_OFFER_SAFE_PATTERNS = [
  '/legal/*',
  '/terms/*',
  '/privacy/*',
  '/tos/*',
  '/cookie-policy/*',
  '/disclaimer/*',
];

export const DEFAULT_CONSENT_SELECTORS = [
  '#accept-cookies',
  '[data-testid="cookie-accept"]',
  '.cookie-consent-accept',
];

// ─── Test Run Types ───

export interface RecipeTestRun {
  id: string;
  recipe_id: string;
  status: 'RUNNING' | 'COMPLETE' | 'FAILED';
  start_url: string;
  started_at: string | null;
  ended_at: string | null;
  duration_ms: number | null;
  error_code: string | null;
  error_message: string | null;
  config_snapshot: FirecrawlRecipeConfig | null;
  created_at: string;
  // Firecrawl-specific
  markdown_preview?: string;
  links_found?: number;
  offer_safe_violations?: string[];
  steps?: RecipeTestStep[];
}

export interface RecipeTestStep {
  id: string;
  step_index: number;
  step_type: string;
  success: boolean;
  duration_ms: number | null;
  evidence_snippet: string | null;
  evidence_json: Record<string, unknown> | null;
  error_code: string | null;
  error_message: string | null;
  match_count: number | null;
  chosen_label: string | null;
}
